import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/id-card.jpg';

function IDCardsDetails() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // shared inline styles (kept consistent with other extended pages)
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'Eligibility & ID Types',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Standard ID:', 'Official state photo identification for residents who do not drive.'],
            ['REAL ID (optional):', 'Meets federal standards for certain uses like domestic air travel and entry to secure federal facilities.'],
            ['Temporary/lawful presence:', 'Non-U.S. citizens may receive a credential reflecting authorized stay when eligible.'],
          ].map(([k, v], i) => (
            <li key={`types-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'REAL ID Overview',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['What it is:', 'A state ID that meets federal security standards; still optional for most daily activities.'],
            ['Air travel:', 'From May 7, 2025, REAL ID or a valid passport is required for U.S. domestic flights and certain federal sites.'],
            ['Same visit upgrade:', 'You can request REAL ID when first applying or at renewal if you bring the required documents.'],
          ].map(([k, v], i) => (
            <li key={`realid-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'How to Apply (First-Time)',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Complete application:', 'Fill out your ID card application online (if available) or in person.'],
            ['Bring required documents:', 'Identity/legal presence, Social Security, and two proofs of state residency.'],
            ['Photo & payment:', 'Your photo is taken at the center; pay applicable fees.'],
          ].map(([k, v], i) => (
            <li key={`apply-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Documents Checklist',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Identity & legal presence:', 'U.S. birth certificate, passport, certificate of naturalization, or DHS immigration documents as applicable.'],
            ['Social Security:', 'SSN card, W-2, 1099, or paystub showing full SSN (requirements vary).'],
            ['Residency proofs:', 'Two documents with your name and address (e.g., utility bill, lease, bank statement); no photocopies.'],
            ['Name changes:', 'If your legal name differs from identity proof, bring marriage certificate, court order, or adoption decree.'],
          ].map(([k, v], i) => (
            <li key={`docs-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Renewals & Replacements',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Renewal options:', 'Online, by mail, or in person (availability depends on your previous renewal and eligibility).'],
            ['Lost or stolen ID:', 'Report if needed and request a replacement with proper identity/residency proofs.'],
            ['Keep info current:', 'Update address within required timeframes to receive notices and your card by mail.'],
          ].map(([k, v], i) => (
            <li key={`renew-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Fees & Possible Reductions',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Standard fees:', 'Application, renewal, and duplicate fees apply; amounts vary by credential and age.'],
            ['Reduced/waived fees:', 'Select groups (e.g., seniors, veterans, low-income) may qualify where authorized.'],
            ['Payment types:', 'Confirm accepted payment methods at your service center.'],
          ].map(([k, v], i) => (
            <li key={`fees-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Minors, Seniors & Special Circumstances',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Minors:', 'Parental/guardian consent and proof of relationship may be required for youth IDs.'],
            ['Seniors:', 'Longer validity or reduced fees may be available; check eligibility.'],
            ['Persons with disabilities:', 'Ask about accommodations, alternative service options, and accessible formats.'],
          ].map(([k, v], i) => (
            <li key={`groups-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Address & Name Changes',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Address change:', 'Update within the required period; bring two new residency proofs if your address changed.'],
            ['Legal name change:', 'Provide certified court/marriage documents and request an updated card.'],
            ['Timelines:', 'Processing and mailing times vary; keep your mailing address current.'],
          ].map(([k, v], i) => (
            <li key={`changes-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'After You Apply',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Interim receipt:', 'You may receive a paper receipt for identification until your card arrives by mail.'],
            ['Verify details:', 'Check your name, address, and any endorsements or designations for accuracy.'],
            ['Keep it secure:', 'Store your ID safely and avoid carrying extra sensitive documents.'],
          ].map(([k, v], i) => (
            <li key={`after-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Accessibility & Language Access',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Accommodations:', 'Request assistance for mobility, vision, hearing, or cognitive needs.'],
            ['Interpreter services:', 'Language support may be available; ask ahead of your visit.'],
            ['Alternate formats:', 'Large-print and translated materials provided where available.'],
          ].map(([k, v], i) => (
            <li key={`access-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <section className="department-details-section">
      <div className="container">
        <div className="row">

          {/* Sidebar */}
          <div className="col-12 col-lg-4 col-xl-4">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            <div className="department-details-imgbox">
              <img src={SubDeptImage} className="img-fluid" alt="State Identification Cards" />
            </div>

            <div className="department-details-content-box">
              <h4 ref={titleRef} className="department-details-title">State Identification Cards</h4>
              {/* Overview (always visible, same color as title but smaller) */}
              <p style={overviewText}>
                Non-driver IDs provide official photo identification for banking, travel, and everyday needs. Bring the
                right documents, choose Standard or optional REAL ID, and keep your address current to receive your card
                without delays.
              </p>
            </div>

            {/* Accordion (expanded information) */}
            <div className="accordian-box">
              {sections.map((item, index) => (
                <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                  <div className="accordian-title">
                    <h5>{item.Que}</h5>
                    <i
                      className="fa-solid fa-angle-right"
                      onClick={() => setFaq(index)}
                      role="button"
                      aria-label={`Toggle ${item.Que}`}
                    />
                  </div>

                  {Faq === index && (
                    <div className="accordian-content" style={{ display: 'block' }}>
                      {item.Answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="department-details-policy-box">
              <h4>Our Commitment:</h4>
              <p>
                We deliver secure IDs with clear requirements and flexible service options—so every resident can obtain
                reliable identification with minimal hassle.
              </p>
            </div>
            <div>
              <Link to="/department/dmv">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Go Back
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default IDCardsDetails;
